<script setup lang="ts">
// The AI-assistant (MCP) switch inside the invite/edit form (v3 `2d9ed3d`). A dashboard grant
// cannot carry the assistant, so the switch reads off and locks there instead of disappearing —
// moving someone onto a dashboard grant visibly takes it away.
import { computed } from 'vue'

import type { AssistantSeats, MemberScope } from '../../models/member.types'
import { assistantAllowed } from '../../service/inviteDraft'

import { useI18n } from '@/shared/i18n'
import { Checkbox } from '@/shared/ui'

const enabled = defineModel<boolean>({ required: true })

const props = defineProps<{
  scope: MemberScope
  seats: AssistantSeats | null
  disabled?: boolean
}>()

const { t } = useI18n()

const K = 'workspaceSettings.permissions.invite.assistant'

const inScope = computed(() => assistantAllowed(props.scope))

const seatLine = computed(() => {
  const seats = props.seats
  if (!seats) return undefined
  return seats.limit === null
    ? t(`${K}.seatsUnlimited`, { used: seats.used }, seats.used)
    : t(`${K}.seats`, { used: seats.used, limit: seats.limit })
})
</script>

<template>
  <div class="grid gap-2xs rounded-md border border-border p-sm" data-testid="invite-assistant">
    <Checkbox
      :model-value="enabled && inScope"
      :label="t(`${K}.label`)"
      :disabled="props.disabled || !inScope"
      data-testid="invite-assistant-toggle"
      @update:model-value="enabled = $event"
    />
    <p class="m-0 text-muted text-xs">
      {{ inScope ? t(`${K}.description`) : t(`${K}.dashboardScope`) }}
    </p>
    <p v-if="seatLine" class="m-0 text-muted text-xs" data-testid="invite-assistant-seats">
      {{ seatLine }}
    </p>
  </div>
</template>
